import { create } from 'zustand'

export type ModalState =
  | { type: 'createChannel' }
  | { type: 'browseChannels' }
  | { type: 'newMessage' }
  | { type: 'editProfile' }
  | { type: 'setStatus' }
  | { type: 'reminders' }
  | { type: 'scheduled' }
  | { type: 'files' }
  | { type: 'addBookmark'; channel: string }
  | { type: 'invite'; channel: string }
  | { type: 'confirm'; title: string; body: string; confirm: string; danger?: boolean; onConfirm: () => Promise<unknown> | void }
  | { type: 'shortcuts' }
  | { type: 'switcher'; query?: string }
  /** Create a sidebar section (optionally moving `conv` into it), or rename section `id`. */
  | { type: 'section'; id?: string; conv?: string }
  | { type: 'aiOrganize' }

export type SidePanel =
  | { type: 'thread'; channel: string; ts: string }
  | { type: 'details'; channel: string; tab?: 'about' | 'members' | 'pins' | 'bookmarks' | 'settings' }
  | { type: 'profile'; user: string }

export type MainView = { type: 'channel' } | { type: 'search'; query: string } | { type: 'home' }

export interface EditingState {
  channel: string
  ts: string
}

interface UiState {
  channelId: string | null
  view: MainView
  panel: SidePanel | null
  modal: ModalState | null
  editing: EditingState | null
  /** Message to scroll to and flash after navigation (e.g. from search). */
  highlight: { channel: string; ts: string } | null
  drafts: Record<string, string>
  unread: Record<string, number>
  mentions: Record<string, number>
  realtime: 'connecting' | 'live' | 'polling'
  toast: { id: number; text: string; tone?: 'error' } | null
  /** Conversation id → when it was last opened (ms), for the quick switcher. */
  recent: Record<string, number>
  /** Conversation id → when its unread state last changed live (ms); newer than a scan wins. */
  touched: Record<string, number>
  /** Conversation id → newest known message ts (from the launch scan), for "Mark as read". */
  latest: Record<string, string>
  /** Conversation id → when its last message was posted (ms). Drives the 30-day DM list; Slack's `updated` isn't reliable for this. */
  lastMessage: Record<string, number>

  openChannel(id: string, opts?: { highlight?: string }): void
  goHome(): void
  setView(view: MainView): void
  openPanel(panel: SidePanel | null): void
  openModal(modal: ModalState | null): void
  setEditing(editing: EditingState | null): void
  setDraft(key: string, text: string): void
  bumpUnread(channel: string, mention: boolean): void
  clearUnread(channel: string): void
  setRealtime(state: UiState['realtime']): void
  showToast(text: string, tone?: 'error'): void
  setRecent(recent: Record<string, number>): void
  /** Records a message time (ts in seconds) for a conversation; keeps the newest. */
  noteMessage(channel: string, ts: string | number | undefined): void
  setLastMessages(map: Record<string, number>): void
  /** Applies a launch-scan result unless the conversation changed live since `since`. */
  setScannedUnread(channel: string, unread: number, mentions: number, latest: string | undefined, since: number): void
  reset(channelId?: string | null): void
}

let toastId = 0

const MAX_RECENT = 30
function trimRecent(recent: Record<string, number>): Record<string, number> {
  const entries = Object.entries(recent).filter(([, t]) => typeof t === 'number')
  return entries.length <= MAX_RECENT ? Object.fromEntries(entries) : Object.fromEntries(entries.sort((a, b) => b[1] - a[1]).slice(0, MAX_RECENT))
}

export const useUi = create<UiState>((set) => ({
  channelId: null,
  view: { type: 'home' },
  panel: null,
  modal: null,
  editing: null,
  highlight: null,
  drafts: {},
  unread: {},
  mentions: {},
  realtime: 'connecting',
  toast: null,
  recent: {},
  touched: {},
  latest: {},
  lastMessage: {},

  openChannel: (id, opts) =>
    set((s) => ({
      channelId: id,
      view: { type: 'channel' },
      editing: null,
      highlight: opts?.highlight ? { channel: id, ts: opts.highlight } : null,
      panel: s.panel?.type === 'details' ? { type: 'details', channel: id } : s.panel?.type === 'thread' ? null : s.panel,
      unread: { ...s.unread, [id]: 0 },
      mentions: { ...s.mentions, [id]: 0 },
      recent: trimRecent({ ...s.recent, [id]: Date.now() }),
      touched: { ...s.touched, [id]: Date.now() },
    })),
  goHome: () => set({ view: { type: 'home' }, panel: null }),
  setView: (view) => set((s) => ({ view, panel: view.type === 'channel' || s.panel?.type !== 'details' ? s.panel : null })),
  openPanel: (panel) => set({ panel }),
  openModal: (modal) => set({ modal }),
  setEditing: (editing) => set({ editing }),
  setDraft: (key, text) => set((s) => ({ drafts: { ...s.drafts, [key]: text } })),
  bumpUnread: (channel, mention) =>
    set((s) => ({
      touched: { ...s.touched, [channel]: Date.now() },
      unread: { ...s.unread, [channel]: (s.unread[channel] ?? 0) + 1 },
      mentions: mention ? { ...s.mentions, [channel]: (s.mentions[channel] ?? 0) + 1 } : s.mentions,
    })),
  clearUnread: (channel) =>
    set((s) => ({ unread: { ...s.unread, [channel]: 0 }, mentions: { ...s.mentions, [channel]: 0 }, touched: { ...s.touched, [channel]: Date.now() } })),
  noteMessage: (channel, ts) =>
    set((s) => {
      const ms = Math.round(Number(ts) * 1000)
      if (!Number.isFinite(ms) || ms <= (s.lastMessage[channel] ?? 0)) return s
      return { lastMessage: { ...s.lastMessage, [channel]: ms } }
    }),
  setLastMessages: (map) => set((s) => ({ lastMessage: { ...map, ...s.lastMessage } })),
  setScannedUnread: (channel, unread, mentions, latest, since) =>
    set((s) => {
      // The last-message time always applies, even if the unread state changed live meanwhile.
      const ms = latest ? Math.round(Number(latest) * 1000) : 0
      const lastMessage = ms > (s.lastMessage[channel] ?? 0) ? { ...s.lastMessage, [channel]: ms } : s.lastMessage
      if ((s.touched[channel] ?? 0) > since) return { lastMessage }
      // The conversation you're looking at is read.
      const viewing = s.channelId === channel && s.view.type === 'channel'
      return {
        lastMessage,
        unread: { ...s.unread, [channel]: viewing ? 0 : unread },
        mentions: { ...s.mentions, [channel]: viewing ? 0 : mentions },
        latest: latest ? { ...s.latest, [channel]: latest } : s.latest,
      }
    }),
  setRealtime: (realtime) => set({ realtime }),
  showToast: (text, tone) => {
    const id = ++toastId
    set({ toast: { id, text, tone } })
    setTimeout(() => set((s) => (s.toast?.id === id ? { toast: null } : s)), 3500)
  },
  setRecent: (recent) => set({ recent: trimRecent(recent) }),
  reset: (channelId = null) =>
    set({ channelId, view: channelId ? { type: 'channel' } : { type: 'home' }, panel: null, modal: null, editing: null, highlight: null, unread: {}, mentions: {}, drafts: {}, recent: {}, touched: {}, latest: {}, lastMessage: {} }),
}))
